import React, { useEffect } from "react";

import { useMutation } from "@apollo/client";
import { useParams } from "react-router-dom";

import PageContent from "components/PageContent";
import Panel from "components/Panel";
import PanelSection from "components/PanelSection";
import { useRoutes } from "utils/routes";

import {
  MemberAcceptInvite,
  MemberAcceptInviteVariables,
} from "./types/MemberAcceptInvite";

import MUTATION from "./Mutation.graphql";
import { useAsyncActionFlash } from "components/WithFlashes";

const MemberDetails: React.FunctionComponent = () => {
  const { organization } = useRoutes();
  const { slug, inviteToken } = useParams();
  const [acceptInvite, { called, loading, error }] = useMutation<
    MemberAcceptInvite,
    MemberAcceptInviteVariables
  >(MUTATION);
  useAsyncActionFlash({
    called,
    loading,
    error: sanitizeError(error?.message),
    success: "Accepted invitation!",
  });

  const handleAcceptClick = () => {
    acceptInvite({
      variables: { organizationSlug: slug, inviteToken },
    });
  };

  // force a full-page reload to fetch the new organization data
  const reloadDest =
    called && !loading && !error ? organization(slug) : undefined;
  useEffect(() => {
    if (!reloadDest) {
      return;
    }
    window.location.href = reloadDest;
  });

  return (
    <PageContent
      title="Organization Member: Accept Invitation"
      pageCategory="organization-members"
      pageName="accept"
    >
      <Panel title="Accept Invitation">
        <PanelSection>
          You've been invited to join the organization <strong>{slug}</strong>
          <div className="mt-2">
            <button onClick={handleAcceptClick} className="btn btn-success">
              Accept
            </button>
          </div>
        </PanelSection>
      </Panel>
    </PageContent>
  );
};

function sanitizeError(msg: string | undefined) {
  if (!msg) {
    return undefined;
  }
  if (msg.startsWith("Could not find")) {
    // If a user mis-copies the invite code, they could receive an internal
    // error like "Could not find organizationmembership record." Let's sanitize
    // that for clarity:
    return "Could not find invite";
  }
  return msg;
}

export default MemberDetails;
